<template>
  <div class="website-layout">
    <BaseHeader :type="headerType">
      <template v-if="headerData" #custom-content>
        <div class="website-layout__header-container">
          <nav
            v-if="headerData.breadcrumb"
            class="website-layout__breadcrumb"
            aria-label="breadcrumb"
          >
            <template v-for="(item, index) in headerData.breadcrumb" :key="index">
              <NuxtLink v-if="item.to" :to="item.to" class="website-layout__breadcrumb-link">
                {{ item.label }}
              </NuxtLink>
              <span v-else class="website-layout__breadcrumb-current">{{ item.label }}</span>
              <span
                v-if="index < headerData.breadcrumb.length - 1"
                class="website-layout__breadcrumb-sep"
                >/</span
              >
            </template>
          </nav>
          <h1 v-if="headerData.title" class="website-layout__title">{{ headerData.title }}</h1>
        </div>
      </template>
    </BaseHeader>

    <main
      class="website-layout__main"
      :class="{
        'has-custom-header': hasCustomHeader,
      }"
    >
      <slot />
    </main>

    <!-- 客服机器人组件：仅客户端挂载，SSR 不渲染 -->
    <ClientOnly>
      <BaseChatbot />
    </ClientOnly>

    <BaseFooter />
  </div>
</template>

<script setup>
/**
 * @description Official website layout
 */

import { computed } from 'vue'

const route = useRoute()
const headerType = computed(() => route.meta.headerType || 'default')
const headerData = computed(() => route.meta.headerData || null)

/**
 * @description 是否有自定义头部
 */
const hasCustomHeader = computed(() => headerType.value === 'custom' && headerData.value)
</script>

<style lang="scss" scoped>
.website-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;

  &__main {
    flex: 1;
    width: 100%;
    padding-top: $base-header-height;

    &.has-custom-header {
      padding-top: calc($base-header-height + 100px);

      @include respond-to-max('lg') {
        padding-top: calc($base-header-height-mobile + 80px);
      }
    }
  }

  @include respond-to-max('lg') {
    &__main {
      padding-top: $base-header-height-mobile;
    }
  }

  &__header-container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding-bottom: 20px;

    @include respond-to-max('lg') {
      padding-bottom: 16px;
    }

    @include respond-to('xxl') {
      max-width: 1440px;
    }

    @include respond-to('xxxl') {
      max-width: 1600px;
    }
  }

  &__breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding: 0;
    font-size: 14px;
    color: $text-subtitle-color;
    line-height: 1.5;

    @include respond-to-max('lg') {
      margin-bottom: 12px;
      font-size: 13px;
    }
  }

  &__breadcrumb-link {
    color: $text-subtitle-color;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: $primary-color;
    }
  }

  &__breadcrumb-sep {
    color: $text-subtitle-color;
  }

  &__breadcrumb-current {
    color: $text-title-color;
  }

  &__title {
    font-size: 32px;
    font-weight: 700;
    text-align: center;
    color: $text-title-color;
    margin: 0;
    line-height: 1.4;

    @include respond-to-max('lg') {
      font-size: 24px;
    }
  }
}
</style>
